/**
 * Meta Pixel (Facebook Pixel) - Production Implementation
 * Integrates with consent management and follows Meta best practices
 */

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const metaPixelId = config.public.metaPixelId

  if (!metaPixelId || process.server) {
    return
  }

  // Initialize Meta Pixel with consent mode
  const initializeMetaPixel = () => {
    // Create the script
    const script = document.createElement('script')
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    `
    document.head.appendChild(script)

    // Initialize with the pixel ID
    window.fbq?.('init', metaPixelId, {
      external_id: 'your_external_id' // Optional: for advanced matching
    })

    // Set default consent to revoked (GDPR compliance)
    window.fbq?.('consent', 'revoke')

    // Add noscript fallback
    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1" />`
    document.body.appendChild(noscript)

    console.log('[Meta Pixel] Initialized with consent revoked')
  }

  // Handle consent changes
  const handleConsentChange = () => {
    const { $consentMode } = useNuxtApp()
    const consentStatus = $consentMode?.getStatus()

    if (consentStatus === 'accepted' && window.fbq) {
      window.fbq('consent', 'grant')
      window.fbq('track', 'PageView')
      console.log('[Meta Pixel] Consent granted, tracking enabled')
    } else if (consentStatus === 'rejected' && window.fbq) {
      window.fbq('consent', 'revoke')
      console.log('[Meta Pixel] Consent revoked, tracking disabled')
    }
  }

  // Initialize on mount
  onMounted(() => {
    initializeMetaPixel()

    // Check initial consent status
    setTimeout(() => {
      handleConsentChange()
    }, 500)

    // Listen for consent changes
    document.addEventListener('cookie-consent-granted', () => {
      if (window.fbq) {
        window.fbq('consent', 'grant')
        window.fbq('track', 'PageView')
        console.log('[Meta Pixel] Consent granted via event')
      }
    })

    document.addEventListener('cookie-consent-denied', () => {
      if (window.fbq) {
        window.fbq('consent', 'revoke')
        console.log('[Meta Pixel] Consent denied via event')
      }
    })
  })

  // Track route changes for SPA navigation
  const router = useRouter()
  router.afterEach(() => {
    nextTick(() => {
      const { $consentMode } = useNuxtApp()
      const consentStatus = $consentMode?.getStatus()

      if (consentStatus === 'accepted' && window.fbq) {
        window.fbq('track', 'PageView')
      }
    })
  })

  // Provide global access to fbq function with consent check
  return {
    provide: {
      metaPixel: {
        track: (eventName: string, parameters?: object) => {
          const { $consentMode } = useNuxtApp()
          const consentStatus = $consentMode?.getStatus()

          if (consentStatus === 'accepted' && window.fbq) {
            window.fbq('track', eventName, parameters)
            console.log(`[Meta Pixel] Tracked event: ${eventName}`, parameters)
          } else {
            console.log(`[Meta Pixel] Event not tracked (no consent): ${eventName}`)
          }
        },
        trackCustom: (eventName: string, parameters?: object) => {
          const { $consentMode } = useNuxtApp()
          const consentStatus = $consentMode?.getStatus()

          if (consentStatus === 'accepted' && window.fbq) {
            window.fbq('trackCustom', eventName, parameters)
            console.log(`[Meta Pixel] Tracked custom event: ${eventName}`, parameters)
          } else {
            console.log(`[Meta Pixel] Custom event not tracked (no consent): ${eventName}`)
          }
        }
      }
    }
  }
})
